import { Injectable } from '@angular/core';
import { OrderControllerService, OrderItem, PaymentControllerService, ReceiptDto } from './gen';
import * as dayjs from 'dayjs';
import { AuthService } from './auth.service';
import OrderTypeEnum = OrderItem.OrderTypeEnum;
import { FeatureService } from './feature.service';

@Injectable({
  providedIn: 'root',
})
export class ReceiptService {
  constructor(
    private paymentService: PaymentControllerService,
    private featureService: FeatureService,
    private customAuthService: AuthService
  ) { }

  getPaymentReceiptHtml(paymentId: number, lineLength: number, width: number, cb: (res) => void) {
    const drawText =
      (textAlign = 'text-left') =>
        (text) => {
          return `<tr><td colspan="99"  class="${textAlign}">${text ? text : '&nbsp'}</td></tr>`;
        };

    const underLine = '-'.repeat(lineLength);
    const space = '&nbsp'.repeat(lineLength);

    this.paymentService
      .getPaymentReceiptGet(paymentId, this.customAuthService.getUser().userId)
      .subscribe((receipt: ReceiptDto) => {
        if (receipt == null) {
          cb('');
          return;
        }

        let textToPrint = '';


        // if(receipt.buzzerNo){
        //   textToPrint += '<b>Buzzer No: '+receipt.buzzerNo+'</b>';
        // }

        textToPrint += this.buildHeader(receipt, 'receipt', drawText('text-center'));

        let subtotal = 0;

        textToPrint += this.formatReceiptRow('Item', 'Price(RM)', 'Qty');

        textToPrint += drawText()(underLine);

        let itemNo = 0;
        receipt.receiptItems.forEach((item, currentIdx) => {
          itemNo = itemNo + 1;
          textToPrint += this.formatReceiptRow('(' + itemNo + ') ' + item.name, item.total?.toFixed(2), item.quantity);

          if (item?.discountAmount > 0) {
            textToPrint += this.formatReceiptRow(
              `Disc`,
              `(${item?.discountPercentage}%) -${item?.discountAmount?.toFixed(2)}`
            );
          }

          // Display per-item tax information
          if (item.inclusiveTaxAmount && item.inclusiveTaxAmount > 0) {
            textToPrint += this.formatReceiptRow(`  SST (6%) - Inclusive`, item.inclusiveTaxAmount?.toFixed(2));
            textToPrint += this.formatReceiptRow(`  Net price`, item.netPrice?.toFixed(2));
          } else if (item.taxAmount && item.taxAmount > 0) {
            textToPrint += this.formatReceiptRow(`  SST (6%)`, item.taxAmount?.toFixed(2));
          }

          if (item.instructions) {
            textToPrint += drawText()(`<span style="font-size: 12px">&nbsp&nbsp"${item.instructions.trim()}"</span>`);
          }
          if (item.variant) {
            textToPrint += drawText()(
              `<span style="font-size: 12px">&nbsp&nbsp${item.variant?.split('\n').join(' <br> &nbsp&nbsp')}</span>`
            );
          }

          textToPrint += drawText()(space);
          // textToPrint += drawText()(underLine);
          subtotal += item.total;
        });

        textToPrint += drawText()(underLine);
        textToPrint += this.formatReceiptRow('Subtotal RM: ', subtotal?.toFixed(2));

        if (receipt.discountAmount?.toFixed(2) !== '0.00') {
          textToPrint += this.formatReceiptRow('Disc RM: ', '-' + receipt.discountAmount?.toFixed(2));
        }
        if (receipt.discountItemAmount?.toFixed(2) !== '0.00') {
          textToPrint += this.formatReceiptRow('Item Disc RM: ', '- ' + receipt.discountItemAmount?.toFixed(2));
        }
        if (receipt.tax1Amount?.toFixed(2) !== '0.00') {
          textToPrint += this.formatReceiptRow('SST(6%) RM: ', receipt.tax1Amount?.toFixed(2));
        }
        if (receipt.tax2Amount?.toFixed(2) !== '0.00') {
          textToPrint += this.formatReceiptRow('Serv(10%) RM: ', receipt.tax2Amount?.toFixed(2));
        }
        if (receipt.tipsAmount?.toFixed(2) !== '0.00') {
          textToPrint += this.formatReceiptRow('Tips RM: ', receipt.tipsAmount?.toFixed(2));
        }
        if (receipt.membershipAppliedPoint?.toFixed(2) !== '0.00') {
          textToPrint += this.formatReceiptRow('Cashb. Disc RM: ', '-' + receipt.membershipAppliedPoint?.toFixed(2));
        }
        if (receipt.roundingAmount?.toFixed(2) !== '0.00') {
          textToPrint += this.formatReceiptRow('Rounding RM: ', receipt.roundingAmount?.toFixed(2));
        }

        textToPrint += drawText()(underLine);

        textToPrint += this.formatReceiptRow('Total RM: ', receipt.netAmount?.toFixed(2));
        if (receipt.inclusiveTax?.toFixed(2) !== '0.00') {
          textToPrint += this.formatReceiptTextRow(`(Inclusive of 6% SST: RM${receipt?.inclusiveTax.toFixed(2)})`, '');
        }
        textToPrint += this.formatReceiptRow('Received RM: ', receipt.paidAmount?.toFixed(2));
        textToPrint += this.formatReceiptRow('Change RM: ', receipt.changeAmount?.toFixed(2));

        // Display payment method(s)
        if (receipt.staffMealUsed && receipt.staffMealAmount > 0) {
          // Mixed payment: both staff meal and other payment method
          textToPrint += this.formatReceiptRow('Paid using: ', 'Staff Meal');
          if (receipt.paidAmount > receipt.staffMealAmount) {
            textToPrint += this.formatReceiptRow('              ', receipt.paymentMethodName || '-');
          }
        } else if (receipt.voucherUsed && receipt.voucherAmount > 0) {
          // Mixed payment: both voucher and other payment method
          textToPrint += this.formatReceiptRow('Paid using: ', 'Voucher');
          if (receipt.paidAmount > receipt.voucherAmount) {
            textToPrint += this.formatReceiptRow('              ', receipt.paymentMethodName || '-');
          }
        } else {
          // Single payment method
          textToPrint += this.formatReceiptRow('Paid using: ', receipt.paymentMethodName || '-');
        }

        textToPrint += this.formatReceiptRow(
          'Ref: ',
          receipt.ewalletReferenceNo ? receipt.ewalletReferenceNo.toString() : '-'
        );

        // Staff meal details (only if staff meal was used)
        if (receipt.staffMealUsed && receipt.staffMealAmount > 0) {
          textToPrint += drawText()(underLine);
          textToPrint += this.formatReceiptTextRow('Staff Reference: ', receipt.staffNo || '-');
          textToPrint += this.formatReceiptRow('Staff Meal Balance: RM', receipt.staffMealBalance?.toFixed(2) || '0.00');
          textToPrint += drawText()(underLine);
        }

        // Voucher details (only if voucher was used)
        if (receipt.voucherUsed && receipt.voucherAmount > 0) {
          textToPrint += drawText()(underLine);
          textToPrint += this.formatReceiptTextRow('Voucher Code: ', receipt.voucherCode || '-');
          if (receipt.voucherName) {
            textToPrint += this.formatReceiptTextRow('Voucher: ', receipt.voucherName);
          }
          textToPrint += this.formatReceiptRow('Voucher Discount: RM', '-' + receipt.voucherAmount?.toFixed(2));
          textToPrint += drawText()(underLine);
        }

        if (receipt.membershipName) {
          textToPrint += drawText()(underLine);
          textToPrint += this.formatReceiptTextRow('Cust. Name: ', receipt.membershipName);
          textToPrint += this.formatReceiptTextRow('Cust. Phone: ', receipt.membershipPhoneNo.toString());
          textToPrint += this.formatReceiptRow('Cashb. RM: ', receipt.membershipCashback?.toFixed(2));
          textToPrint += drawText()(underLine);
        }

        textToPrint += drawText()('');

        if (receipt.qrCode) {
          textToPrint += drawText()(space);
          textToPrint += drawText()(space);
          var qrText =
            '         <tr><td colspan="99"  class="text-left"><tr>\n' +
            '        <td colspan="99" class="text-center">\n' +
            '          Scan to claim your cashback! \n' +
            '        </td>';

          textToPrint += drawText()(qrText);
          textToPrint += drawText()(space);
          var qrImage =
            '<tr>\n' +
            '        <td colspan="99" class="text-center">\n' +
            '          <img style="width: 300px;" src="' +
            receipt.qrCode +
            '" />\n' +
            '        </td>\n' +
            '      </tr>';

          textToPrint += drawText()(qrImage);

          var qrText2 =
            '<tr><td colspan="99"  class="text-left"><tr>\n' +
            '        <td colspan="99" class="text-center">\n' +
            '          *Use this cashback on your next visit.*.\n' +
            '        </td>';

          textToPrint += drawText()(space);
          textToPrint += drawText()(qrText2);
        }

        textToPrint += drawText()(space);
        textToPrint += this.buildFooter(receipt, drawText('text-center'));
        textToPrint += drawText()('');
        textToPrint += drawText()(space);
        textToPrint += drawText()(space);
        textToPrint += drawText()(space);

        let content = `
        <!DOCTYPE html>
        <html lang="en">
          <head>
            <meta charset="UTF-8" />
            <meta name="viewport" content="width=device-width, initial-scale=1.0" />
            <title>Document</title>
            <style>
              * {
                margin: 0;
                padding: 0;
              }

              body {
                width: ${width}px;
              }
              .text-left {
                text-align: left;
              }

              .text-center {
                text-align: center;
              }

              .text-right {
                text-align: right;
              }

              td {
                vertical-align: top;
                padding: 0 1px;
              }

              .logo {
                height: 100px;
              }
            </style>
          </head>
          <body style="margin: 0 auto">
            <table style="border: 0px solid black">${textToPrint}</table>
          </body>
        </html>`;

        console.log('=== PAYMENT RECEIPT HTML (dpos-pos-ui) ===');
        console.log(content);
        console.log('===========================================');

        cb(content);
      });
  }

  getOrderReceiptHtml(
    merchantId: number,
    kitchenId: number,
    posId: number,
    prepareType: OrderTypeEnum,
    lineLength: number,
    width: number,
    cb: (res) => void
  ) {
    let subscription = this.paymentService
      .getOrderReceiptUnprintedGet(kitchenId, merchantId, posId, prepareType)
      .subscribe(
        (receipts) => {
          const drawText =
            (textAlign = 'text-left') =>
              (text) => {
                return `<tr><td colspan="2"  class="${textAlign}">${text ? text : '&nbsp'}</td></tr>`;
              };

          const underLine = '-'.repeat(lineLength);

          const space = '&nbsp'.repeat(lineLength);

          for (let receipt of receipts) {
            let textToPrint = '';

            if (receipt.receiptItems && receipt.receiptItems.length > 0) {
              textToPrint += drawText()(space);
              textToPrint += drawText()(space);
              textToPrint += drawText()(space);
              textToPrint += drawText()(space);
              textToPrint += drawText()(space);
              textToPrint += drawText()(space);

              // New header format
              const orderSegment = receipt?.receiptNo?.split('-').pop() || receipt?.receiptNo || '';
              if (prepareType) {
                textToPrint += drawText()(`<h1 style="text-align: center">${prepareType.replace('_', ' ')}</h1>`);
              }
              textToPrint += drawText()(`<h2>#${orderSegment}</h2>`);
              textToPrint += drawText()(space);
              textToPrint += drawText()(`Table: <h3 style="display: inline">${receipt.tableName}</h3>`);
              textToPrint += drawText()('Kitchen: ' + receipt?.kitchenName);
              textToPrint += drawText()('Date: ' + dayjs(receipt?.paymentDate).format('DD/MM/YYYY HH:mm'));

              textToPrint += drawText()(space);
              textToPrint += drawText()(space);

              textToPrint += drawText()(underLine);

              receipt.receiptItems.forEach((item, currentIdx) => {
                if (this.featureService.isFeatureEnabled('orderItemRowPrefixQty')) {
                  textToPrint += this.formatOrderItemRow(`${item.printQuantity}x - ${item.name}`);
                } else {
                  textToPrint += this.formatOrderItemRow(`- ${item.name}`, `x${item.printQuantity}`);
                }

                if (item.setItem) {
                  textToPrint += drawText()(
                    `<span style="font-size: 19px;">` +
                    item?.setItem
                      ?.split('\n')
                      .map((line) => `&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;${this.toTitleCase(line)}`)
                      .join('<br>') +
                    `</span>`
                  );
                  // textToPrint += drawText()(item?.variant?.split('\n').join(' / '));
                }

                if (item.instructions) {
                  textToPrint += drawText()(
                    `<span style="font-size: 19px">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;"${item.instructions}"</span>`
                  );
                }

                if (item.variant) {
                  textToPrint += drawText()(
                    `<span style="font-size: 19px">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;"${item?.variant
                      ?.split('\n')
                      .join(' / ')}"</span>`
                  );
                  // textToPrint += drawText()(item?.variant?.split('\n').join(' / '));
                }

                textToPrint += drawText()(space);
              });
            } else {
            }

            textToPrint += drawText()(space);
            textToPrint += drawText()(space);
            textToPrint += drawText()(space);
            textToPrint += drawText()(space);
            textToPrint += drawText()(space);
            textToPrint += drawText()(space);

            let content = `<!DOCTYPE html>
          <html lang="en">
          <head>
            <meta charset="UTF-8" />
            <meta name="viewport" content="width=device-width, initial-scale=1.0" />
            <title>Document</title>
            <style>
              * {
                margin: 0;
                padding: 0;
              }

              body {
                width: ${width}px;
                max-width: ${width}px;
                overflow: hidden;
              }
              .text-left {
                text-align: left;
              }

              .text-center {
                text-align: center;
              }

              .text-right {
                text-align: right;
              }

              table {
                table-layout: fixed;
                width: 100%;
              }

              td {
                vertical-align: top;
                padding: 0 1px;
                word-wrap: break-word;
                overflow-wrap: break-word;
                word-break: break-word;
              }

              .logo {
                height: 100px;
              }
            </style>
          </head>
          <body style="margin: 0 auto">
             <table style="width: 100%; border: 0px solid black">${textToPrint}</table>
          </body>
          </html>`;

            console.log('=== KITCHEN RECEIPT HTML (dpos-pos-ui) ===');
            console.log(content);
            console.log('===========================================');
            cb(content);
          }
        },
        (error) => { },
        () => {
          subscription.unsubscribe();
        }
      );
  }
  toTitleCase(str: string): string {
    return str.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase());
  }
  formatReceiptRow(label, amount?, qty?) {
    let formattedAmount = '';
    if (amount) {
      if (isNaN(amount)) {
        formattedAmount = amount;
      } else {
        formattedAmount = parseFloat(amount)
          ?.toFixed(2)
          .replace(/(\d)(?=(\d{3})+\.)/g, '$1,');
      }
    } else {
      formattedAmount = '0.00';
    }

    if (qty) {
      return `
        <tr>
          <td style="width: 60%">${label}</td>
          <td style="width: 10%" class="text-right">${qty}</td>
          <td style="width: 30%" class="text-right">${formattedAmount}</td>
        </tr>
    `;
    } else {
      return `
        <tr>
          <td style="width: 80%" colspan="2">${label}</td>
          <td style="width: 20%" class="text-right">${formattedAmount}</td>
        </tr>
    `;
    }
  }

  formatReceiptTextRow(label, text1?, text2?) {
    if (text2) {
      return `
        <tr>
          <td style="width: 60%">${label}</td>
          <td style="width: 10%" class="text-right">${text1}</td>
          <td style="width: 30%" class="text-right">${text2}</td>
        </tr>
    `;
    } else {
      return `
        <tr>
          <td style="width: 80%" colspan="2">${label}</td>
          <td style="width: 20%" class="text-right">${text1}</td>
        </tr>
    `;
    }
  }

  formatOrderItemRow(label, qty?) {
    if (qty) {
      return `
        <tr>
          <td style="width: 70%; font-size: 22px">${label}</td>
          <td style="width: 10%; font-size: 22px" class="text-right">${qty}</td>
        </tr>
    `;
    } else {
      return `
        <tr>
          <td style="width: 80%; font-size: 22px" colspan="2">${label}</td>
        </tr>
    `;
    }
  }

  private buildHeader(receipt: ReceiptDto, receiptType: 'receipt' | 'bill' | 'order', drawText) {
    let headerContent: string = '';
    if (receipt.header && receiptType === 'receipt') {
      let buzzerNo = '';
      if (receipt.buzzerNo) {
        buzzerNo = receipt.buzzerNo;
      } else {
        buzzerNo = this.extractShortOrderNo(receipt.orderNo) || receipt.queueNo;
      }

      let headerTpl = receipt.header
        .replace('{{merchantLogo}}', receipt.merchantLogo)
        .replace('{{storeName}}', receipt.storeName ?? '-')
        .replace('{{companyName}}', receipt.companyName ?? '-')
        .replace('{{companyPhone}}', receipt.companyPhone)
        .replace('{{companyAddress}}', receipt.companyAddress)
        .replace('{{companyEmail}}', receipt.companyEmail)
        .replace('{{companyRegNo}}', receipt.companyRegNo || '')
        .replace('{{receiptNo}}', receipt.receiptNo)
        .replace('{{orderNo}}', receipt.orderNo || '')
        .replace('{{queueNo}}', this.extractShortOrderNo(receipt.orderNo) || receipt.queueNo)
        .replace('{{buzzerNo}}', buzzerNo)
        .replace('{{tableName}}', receipt.tableName)
        .replace('{{orderDate}}', dayjs(receipt.paymentDate).format('DD/MM/YYYY HH:mm'));

      headerTpl
        .split('\n')
        .filter((line) => line)
        .forEach((line) => {
          headerContent += drawText(line);
        });
    } else {
      if (receiptType === 'receipt') {
        if (receipt.merchantLogo) headerContent += drawText(`<img class="logo" src="${receipt.merchantLogo}"/>`);
        headerContent += drawText(`<h2>#${this.extractShortOrderNo(receipt.orderNo) || receipt.queueNo}</h2>`);
      }
      headerContent += drawText(receipt.storeName ?? receipt.companyName);
      headerContent += drawText(receipt.companyPhone ? receipt.companyPhone : '');
      headerContent += drawText(receipt.companyAddress ? receipt.companyAddress : '');

      if (receiptType === 'receipt') {
        headerContent += drawText('Receipt: #' + receipt.receiptNo);
        headerContent += drawText('Table: ' + receipt.tableName);
        headerContent += drawText(dayjs(receipt.paymentDate).format('DD/MM/YYYY HH:mm'));
      } else if (receiptType === 'order') {
        headerContent += drawText('Table: ' + receipt.tableName);
        headerContent += drawText(dayjs(receipt.paymentDate).format('DD/MM/YYYY HH:mm'));
      } else {
        headerContent += drawText('Table: ' + receipt.tableName);
        headerContent += drawText(dayjs(receipt.paymentDate).format('DD/MM/YYYY HH:mm'));
        headerContent += drawText('BILL');
      }
    }

    return headerContent;
  }

  private buildFooter(receipt: ReceiptDto, drawText: (text) => string) {
    let footerContent2: string = '';
    if (receipt.footer) {
      let footerContent = receipt.footer
        .replace('{{storeName}}', receipt.storeName ?? '-')
        .replace('{{companyName}}', receipt.companyName ?? '-')
        .replace('{{companyPhone}}', receipt.companyPhone)
        .replace('{{companyAddress}}', receipt.companyAddress)
        .replace('{{companyEmail}}', receipt.companyEmail)
        .replace('{{receiptNo}}', receipt.receiptNo)
        .replace('{{queueNo}}', this.extractShortOrderNo(receipt.orderNo) || receipt.queueNo)
        .replace('{{tableName}}', receipt.tableName)
        .replace('{{orderDate}}', dayjs(receipt.paymentDate).format('DD/MM/YYYY HH:mm'));

      let strings = footerContent.split('\n');

      strings
        .filter((line) => line)
        .forEach((line) => {
          footerContent2 += drawText(line);
        });
    }

    return footerContent2 ? footerContent2 : drawText('Thank You, Please Come Again') + drawText('Powered by DUCK POS');
  }

  private buildDefaultFooter(drawText: (text) => string) {
    return drawText('Thank You, Please Come Again') + drawText('Powered by DUCK POS');
  }

  private extractShortOrderNo(orderNo: string): string {
    if (!orderNo) return '';
    const parts = orderNo.split('-');
    return parts.length > 1 ? parts[parts.length - 1] : orderNo;
  }
}
